(ns ncr.widgets.pf
  (:require
   ["react" :as react]
   [clojure.string :as str]
   [goog.events :as gevents]
   [goog.events.EventType :as EventType]
   [goog.events.KeyCodes :as KeyCodes]
   [goog.dom :as gdom]
   [goog.object :as gobj]
   [uix.core.alpha :as uix]
   [ncr.etc.pf :as pf]
   [ncr.etc.util :as u]
   [ncr.etc.rhf :as rhf]
   [ncr.widgets.core :as w]))

(defn- item-label [props {:keys [label] :as self}]
  (let [label-fn (or (:label-fn props) (:label-fn self))]
    (or
      (and label-fn (label-fn self)) label (str self))))

(defn- transform-callbacks [props item]
  (let
    [label (item-label props item)
     item (assoc item :label label)]
    (-> item
      (dissoc :label-fn)
      (merge
        {:label label
         "toString" (constantly label)
         "compareTo"
         (fn [other]
           (let [other (js->clj other :keywordize-keys true)]
             (= (get other :id) (get item :id))))}))))

(defn- transform-options [{:keys [filter-fn] :as props} options]
  (->> options
    (map #(transform-callbacks props %))
    (filter
      (fn [{:keys [label]}]
        (or (nil? filter-fn) (filter-fn label))))
    (map
      (fn [{:keys [id class] :as option}]
        {:id id
         :key id
         :class class
         :value option}))
    (map pf/c-select-option)))

(defn- transform-children [{:keys [is-grouped] :as props} children]
  (if is-grouped
    (->> children
      (map
        (fn [{:keys [label key options]}]
          (let [options (transform-options props options)]
            (when-not (empty? options)
              (pf/c-select-group {:label label :key key} options)))))
      (filter some?))
    (transform-options props children)))

(defn- remove-button-click? [event]
  (let
    [el (.-target event)
     remove-button?
     (fn [el]
       (and
         (some-> el .-tagName str/lower-case (= "button"))
         (some-> el (gobj/getValueByKeys "dataset" "ouiaComponentId") str/lower-case (= "remove"))))]
    (or ; 'x' button element tree: button > svg > path
      (remove-button? el)
      (remove-button? (-> el .-parentElement))
      (remove-button? (-> el .-parentElement .-parentElement)))))

(defn- ref->select-el [ref]
  (some-> ^js @ref .-parentRef .-current))

(defn- ref->input-el [ref]
  (some-> ^js @ref .-inputRef .-current))

(defn- use-refocus-input-after-remove [ref]
  (let [was-focused? (uix/state false)]
    (uix/with-effect [@ref]
      (when-let [select-el (ref->select-el ref)]
        (let [listener #(reset! was-focused? (= js/document.activeElement (ref->input-el ref)))]
          (gevents/listen select-el EventType/MOUSEDOWN listener)
          #(gevents/unlisten select-el EventType/MOUSEDOWN listener))))

    (uix/with-effect [@ref]
      (when-let [select-el (ref->select-el ref)]
        (let
          [listener
           (fn [e]
             (when-let [input-el (and @was-focused? (remove-button-click? e) (ref->input-el ref))]
               (.focus input-el)))]
          (gevents/listen select-el EventType/MOUSEUP listener)
          #(gevents/unlisten select-el EventType/MOUSEUP listener))))))

(defn- use-focus-input-on-init [ref focus-input?]
  (let [focus-not-set? (uix/state true)]
    (uix/with-effect [@ref]
      (when-let [el (and focus-input? @focus-not-set? (ref->input-el ref))]
        (.focus el)
        (reset! focus-not-set? false)))))

(defn- use-clear-input-on-esc [ref open?]
  (uix/with-effect [@ref]
    (when-let [input-el (ref->input-el ref)]
      (let
        [listener
         (fn [^js e]
           (when (and @open? (= KeyCodes/ESC (u/key-code e)))
             (.stopPropagation e)
             (.preventDefault e)
             (u/set-input-value (ref->input-el ref) "")
             (reset! open? false)))]
        (gevents/listen input-el EventType/KEYDOWN listener)
        #(gevents/unlisten input-el EventType/KEYDOWN listener)))))

(defn- option-click? [event]
  (let
    [el (.-target event)
     option?
     (fn [el]
       (and
         (some-> el .-tagName str/lower-case (= "button"))
         (some-> el .-classList (.contains "pf-c-select__menu-item"))))]
    (or ; option element tree: button > span > svg > path
      (option? el)
      (option? (-> el .-parentElement))
      (option? (-> el .-parentElement .-parentElement))
      (option? (-> el .-parentElement .-parentElement .-parentElement)))))

(defn- use-refocus-input-after-select [ref]
  (let [was-focused? (uix/state false)]
    (uix/with-effect [@ref]
      (when-let [select-el (ref->select-el ref)]
        (let [listener #(reset! was-focused? (= js/document.activeElement (ref->input-el ref)))]
          (gevents/listen select-el EventType/MOUSEDOWN listener)
          #(gevents/unlisten select-el EventType/MOUSEDOWN listener))))

    (uix/with-effect [@ref]
      (when-let [select-el (ref->select-el ref)]
        (let
          [listener
           (fn [e]
             (when-let [input-el (and @was-focused? (option-click? e) (ref->input-el ref))]
               (.focus input-el)))]
          (gevents/listen select-el EventType/MOUSEUP listener)
          #(gevents/unlisten select-el EventType/MOUSEUP listener))))))

(defn- this-select-event? [ref event]
  (let
    [select-el (ref->select-el ref)
     event-el (.-currentTarget event)]
    (or
      (= select-el event-el)
      (gdom/contains select-el event-el))))

(defn c-pf-select [{:keys [variant focus-input? on-empty-enter] :as props} children]
  (let [multi? (#{:checkbox :typeaheadmulti} variant)
        open? (uix/state false)
        ref (uix/ref)
        on-empty-enter (or on-empty-enter identity)]

    (use-focus-input-on-init ref focus-input?)
    (use-clear-input-on-esc ref open?)
    (use-refocus-input-after-remove ref)
    (use-refocus-input-after-select ref)

    [pf/c-select
     (w/merge-props
       {:is-open @open?
        :ref ref
        :on-toggle
        (fn [open event]
          (if (= :keydown (some-> event .-type keyword))
            (when
              (and
                (= KeyCodes/ENTER (u/key-code event))
                (some-> ref ref->input-el .-value str/blank?))
              (on-empty-enter event))
            (if (this-select-event? ref event)
              (when-not (remove-button-click? event)
                (reset! open? open))
              (reset! open? open))))}

       (-> props
         (dissoc :focus-input? :on-empty-enter)
         (assoc :on-filter
           (fn [_ pattern]
             (let
               [pattern
                (->> pattern
                  (u/format "(?i).*%s.*" pattern)
                  (re-pattern))]
               (uix/as-element
                 (transform-children
                   (assoc props :filter-fn
                     (partial re-find pattern))
                   children)))))

         (update :selections
           (fn [selection]
             (let [transform (partial transform-callbacks props)]
               (if multi?
                 (->> selection (map transform) (sort-by #(item-label props %)))
                 (transform selection)))))

         (update :on-select
           (fn [original]
             (when original
               (fn [event value is-placeholder?]
                 (original event (js->clj value :keywordize-keys true) is-placeholder?)
                 (reset! open? false)))))))

     (transform-children props children)]))

;; TODO: Looks not unused anywhere. ping @aav.
(defn c-file-upload
  ([name form]
   (c-file-upload {} name form))

  ([props name {:keys [control]}]
   [rhf/c-controller
    (merge
      {:name name
       :control control
       :render
       (fn [props]
         [pf/c-file-upload
          {:type :file
           :filename
           (or (some-> props :field :value .-name) "")

           :on-change
           (fn [file _]
             ((-> props :field :on-change) file))}])}
      props)]))

;; TODO: Looks not unused anywhere. ping @aav.
(defn some-file-upload-file? [file]
  (and
    (some? file)
    (not= file "")))

(defn c-pf-checkbox [props]
  (u/without [name form] props
    [rhf/c-controller
     {:name name
      :control (get form :control)
      :render
      (let [props' props]
        (fn [props]
          [pf/c-checkbox
           (merge
             {:id (uix/memo #(gensym) [])}
             props'
             {:is-checked (get-in props [:field :value])
              :on-change
              (fn [value _]
                ((-> props :field :on-change) value))})]))}]))

; workaround for pf's propagation bug
(defn c-label-group
  ([labels]
   (c-label-group {} labels))
  ([props labels]
   (let [collapsed? (uix/state true)]
     (u/without [num-labels on-toggle] props
       (let
         [labels' (take (or num-labels 3) labels)
          diff (- (count labels) (count labels'))]
         [:div.pf-c-label-group {:class (:class props)}
          [:div.pf-c-label-group__main
           (into
             [:ul.pf-c-label-group__list.pf-c-label-group__list]
             (map
               #(do [:li.pf-c-label-group__list-item.pf-c-label-group__list-item %])
               (concat
                 (if @collapsed?
                   labels' labels)
                 (if (> diff 0)
                   [[pf/c-label
                     {:is-overflow-label true
                      :on-click
                      #(do
                         (let [new-collapsed (not @collapsed?)]
                           (when on-toggle (on-toggle new-collapsed))
                           (reset! collapsed? new-collapsed))
                         (.stopPropagation %))}
                     (if @collapsed?
                       (str diff " more") "Show Less")]]))))]])))))

;; This component is a workaround needed to display tooltip on chip with
;; truncated text -- PF's built-in chip's tooltip not working.
(defn c-chip [text on-click]
  (let
    [show-tooltip? (uix/state false)
     ref (uix/ref)

     chip
     [:div {:ref ref}
      [pf/c-chip
       {:component "button"
        :is-overflow-chip true
        :on-click on-click}
       text]]]

    (uix/effect!
      (fn []
        (when-let [el (some-> @ref (.getElementsByClassName "pf-c-chip__text") first)]
          (reset! show-tooltip? (> el.scrollWidth el.clientWidth)))))

    (if @show-tooltip?
      [pf/c-tooltip
       {:content [:span text]
        :position :auto
        :enable-flip true}
       chip]
      chip)))
